import {useState} from 'react';
import type {
  CourseClassroomAttendanceEnum,
  CourseClassroomAttendeeResultEnum,
} from 'src/api/ProactApiClient';
import {
  useAddCourseClassroomAttendeeBatchMutation,
  useRemoveCourseClassroomAttendeeMutation,
  useUpdateCourseClassroomAttendeeMutation,
} from 'src/redux/queries/courseClassroom';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface UseClassRosterActionsResult {
  rosterBuilderOpened: boolean;
  openRosterBuilder: () => void;
  closeRosterBuilder: () => void;
  isAddingAttendees: boolean;
  handleAttendanceChange: (
    attendeeId: number,
    attendance: CourseClassroomAttendanceEnum
  ) => Promise<void>;
  handleResultChange: (
    attendeeId: number,
    result: CourseClassroomAttendeeResultEnum
  ) => Promise<void>;
  handleRemoveAttendee: (attendeeId: number) => Promise<void>;
  handleAddAttendees: (assignmentIds: number[]) => Promise<void>;
}

/**
 * The roster mutations (per-attendee attendance/result/remove and the batch
 * add-attendees flow) for the Class Workspace roster tab. Owns the roster
 * builder modal's open state since closing it on a successful add is part
 * of the same flow.
 */
export const useClassRosterActions = (
  classroomId: string | undefined
): UseClassRosterActionsResult => {
  const {setAlert} = useAlert();
  const [rosterBuilderOpened, setRosterBuilderOpened] = useState(false);

  const [updateAttendee] = useUpdateCourseClassroomAttendeeMutation();
  const [removeAttendee] = useRemoveCourseClassroomAttendeeMutation();
  const [addAttendeeBatch, {isLoading: isAddingAttendees}] =
    useAddCourseClassroomAttendeeBatchMutation();

  const handleAttendanceChange = async (
    attendeeId: number,
    attendance: CourseClassroomAttendanceEnum
  ) => {
    try {
      await updateAttendee({id: attendeeId, attendance}).unwrap();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleResultChange = async (
    attendeeId: number,
    result: CourseClassroomAttendeeResultEnum
  ) => {
    try {
      await updateAttendee({id: attendeeId, result}).unwrap();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleRemoveAttendee = async (attendeeId: number) => {
    try {
      await removeAttendee(attendeeId).unwrap();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleAddAttendees = async (assignmentIds: number[]) => {
    if (classroomId === undefined) {
      return;
    }
    try {
      await addAttendeeBatch({
        classroomId: parseInt(classroomId),
        assignmentIds,
      }).unwrap();
      setAlert(
        `Added ${assignmentIds.length} attendee${assignmentIds.length === 1 ? '' : 's'} to the roster.`,
        'success'
      );
      setRosterBuilderOpened(false);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return {
    rosterBuilderOpened,
    openRosterBuilder: () => setRosterBuilderOpened(true),
    closeRosterBuilder: () => setRosterBuilderOpened(false),
    isAddingAttendees,
    handleAttendanceChange,
    handleResultChange,
    handleRemoveAttendee,
    handleAddAttendees,
  };
};
